// @ts-nocheck

export const NEARMAPS_LAYER_ID = "nearmaps";

export const MIN_ZOOM_REQUIRED_FOR_NEARMAP = 19;
export const MIN_ZOOM_REQUIRED_FOR_VEXCEL = 19;

export const MIN_ZOOM = 3;

const TILE_SIZE = 256;

type PointType = {
  x: number;
  y: number;
};

function NearMapsPoint(this: PointType, a, b) {
  this.x = a;
  this.y = b;
}

const degreesToRadians = (deg: number) => {
  return deg * (Math.PI / 180);
};

const radiansToDegrees = (rad: number) => {
  return rad / (Math.PI / 180);
};

export const rotateTile = (
  coord: any,
  zoom: number | string,
  heading: number,
) => {
  const numTiles = 1 << zoom; // 2^zoom
  let x, y;
  switch (heading) {
    case 0:
      x = coord.x;
      y = coord.y;
      break;
    case 90:
      x = numTiles - (coord.y + 1);
      y = coord.x;
      break;
    case 180:
      x = numTiles - (coord.x + 1);
      y = numTiles - (coord.y + 1);
      break;
    case 270:
      x = coord.y;
      y = numTiles - (coord.x + 1);
      break;
  }
  return new NearMapsPoint(x, y);
};

type MercatorProjectionType = {
  pixelOrigin: PointType;
  pixelsPerLonDegree: number;
  pixelsPerLatRadian: number;
};

export function MercatorProjection(
  this: MercatorProjectionType,
  worldWidth,
  worldHeight,
) {
  this.pixelOrigin = new NearMapsPoint(worldWidth / 2, worldHeight / 2);
  this.pixelsPerLonDegree = worldWidth / 360;
  this.pixelsPerLatRadian = worldHeight / (2 * Math.PI);
}

MercatorProjection.prototype.fromLatLngToPoint = function (latlng, opt_point) {
  const point = opt_point || new NearMapsPoint(0, 0);
  const origin = this.pixelOrigin;

  let { lat, lng } = latlng;
  if (typeof lat === "function") {
    lat = lat();
  }
  if (typeof lng === "function") {
    lng = lng();
  }

  point.x = origin.x + lng * this.pixelsPerLonDegree;
  const siny = Math.sin(degreesToRadians(lat));
  point.y =
    origin.y +
    0.5 * Math.log((1 + siny) / (1 - siny)) * -this.pixelsPerLatRadian;

  return point;
};

MercatorProjection.prototype.fromPointToLatLng = function (point, _noWrap) {
  const origin = this.pixelOrigin;

  const lng = (point.x - origin.x) / this.pixelsPerLonDegree;
  const latRadians = (point.y - origin.y) / -this.pixelsPerLatRadian;
  const lat = radiansToDegrees(
    2 * Math.atan(Math.exp(latRadians)) - Math.PI / 2,
  );

  return new google.maps.LatLng(lat, lng);
};

let vertProjection;
export function getProjectVert() {
  if (!vertProjection) {
    vertProjection = new MercatorProjection(TILE_SIZE, TILE_SIZE);
  }
  return vertProjection;
}

/**
 * Returns a map center that places `latLng` in the vertical middle of the
 * portion of the map left uncovered by a bottom overlay (e.g. a mobile
 * bottom sheet) while staying horizontally centered.
 *
 * @param latLng - The point to keep visible (e.g. a selected building).
 * @param zoom - The zoom level the map will be at once settled.
 * @param mapHeightPx - Height of the map viewport in pixels.
 * @param coveredFraction - Fraction of the viewport covered from the bottom (0-1).
 */
export function offsetCenterForBottomOverlay(
  latLng: { lat: number; lng: number },
  zoom: number,
  mapHeightPx: number,
  coveredFraction = 0.5,
): { lat: number; lng: number } {
  const projection = getProjectVert();
  const point = projection.fromLatLngToPoint(latLng);
  const scale = Math.pow(2, zoom);
  // Moving the center south by half the covered height leaves the point
  // centered in the visible top portion.
  const pixelOffset = (coveredFraction / 2) * mapHeightPx;
  const adjusted = projection.fromPointToLatLng(
    new NearMapsPoint(point.x, point.y + pixelOffset / scale),
  );
  return { lat: adjusted.lat(), lng: adjusted.lng() };
}

function NearMapsVertToWestTranslation(ll) {
  let pt = getProjectVert().fromLatLngToPoint(ll);
  pt = new NearMapsPoint(pt.y, pt.x);
  ll = getProjectVert().fromPointToLatLng(pt);
  return new google.maps.LatLng(ll.lat(), -ll.lng());
}

function NearMapsWestToVertTranslation(ll) {
  ll = new google.maps.LatLng(ll.lat(), -ll.lng());
  let pt = getProjectVert().fromLatLngToPoint(ll);
  pt = new NearMapsPoint(pt.y, pt.x);
  return getProjectVert().fromPointToLatLng(pt);
}

function NearMapsVertToEastTranslaction(ll) {
  let pt = getProjectVert().fromLatLngToPoint(ll);
  pt = new NearMapsPoint(pt.y, pt.x);
  ll = getProjectVert().fromPointToLatLng(pt);
  return new google.maps.LatLng(-ll.lat(), ll.lng());
}

function NearMapsEastToVertTranslation(ll) {
  ll = new google.maps.LatLng(-ll.lat(), ll.lng());
  let pt = getProjectVert().fromLatLngToPoint(ll);
  pt = new NearMapsPoint(pt.y, pt.x);
  return getProjectVert().fromPointToLatLng(pt);
}

export function adjustCenterForMapType(realCenter, mapTypeId) {
  let ll = new google.maps.LatLng(realCenter.lat, realCenter.lng);
  if (mapTypeId === "West") {
    ll = NearMapsVertToWestTranslation(ll);
  } else if (mapTypeId === "East") {
    ll = NearMapsVertToEastTranslaction(ll);
  } else if (mapTypeId === "South") {
    ll = new google.maps.LatLng(-ll.lat(), -ll.lng());
  }
  return { ...realCenter, lat: ll.lat(), lng: ll.lng() };
}

export function adjustCenterBackForMapType(fakeCenter, mapTypeId) {
  let ll = fakeCenter;
  if (mapTypeId === "West") {
    ll = NearMapsWestToVertTranslation(ll);
  } else if (mapTypeId === "East") {
    ll = NearMapsEastToVertTranslation(ll);
  } else if (mapTypeId === "South") {
    ll = new google.maps.LatLng(-ll.lat(), -ll.lng());
  }
  return ll;
}

export function convertPixelCoordinateToTile(worldCoordinate, zoom) {
  const scale = 1 << zoom;
  return new NearMapsPoint(
    Math.floor((worldCoordinate.x * scale) / TILE_SIZE),
    Math.floor((worldCoordinate.y * scale) / TILE_SIZE),
  );
}
